// Theme Context for managing theme customization

import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { ThemeConfig, DeviceType } from '../types/theme';
import { getActiveThemeCSS, injectThemeCSS } from '../services/themeApi';

export interface ThemeContextType {
  theme: ThemeConfig | null;
  deviceType: DeviceType;
  isLoading: boolean;
  error: string | null;
  refreshTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<ThemeConfig | null>(null);
  const [deviceType, setDeviceType] = useState<DeviceType>('desktop');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detect device type
  useEffect(() => {
    const handleResize = () => {
      const newDeviceType = window.innerWidth >= 768 ? 'desktop' : 'mobile';
      setDeviceType(newDeviceType);
    };

    // Initial detection
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load theme when device type changes
  useEffect(() => {
    const loadTheme = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const css = await getActiveThemeCSS(deviceType);
        injectThemeCSS(css);

        // Parse theme from CSS variables for programmatic access
        const rootStyles = getComputedStyle(document.documentElement);
        const themeConfig: ThemeConfig = {
          colors: {
            primary: rootStyles.getPropertyValue('--color-primary').trim(),
            primaryHover: rootStyles.getPropertyValue('--color-primaryHover').trim(),
            primaryDark: rootStyles.getPropertyValue('--color-primaryDark').trim(),
            bgPrimary: rootStyles.getPropertyValue('--color-bgPrimary').trim(),
            bgSecondary: rootStyles.getPropertyValue('--color-bgSecondary').trim(),
            bgTertiary: rootStyles.getPropertyValue('--color-bgTertiary').trim(),
            bgLight: rootStyles.getPropertyValue('--color-bgLight').trim(),
            textPrimary: rootStyles.getPropertyValue('--color-textPrimary').trim(),
            textSecondary: rootStyles.getPropertyValue('--color-textSecondary').trim(),
            textTertiary: rootStyles.getPropertyValue('--color-textTertiary').trim(),
            textOnDark: rootStyles.getPropertyValue('--color-textOnDark').trim(),
            textOnDarkSecondary: rootStyles.getPropertyValue('--color-textOnDarkSecondary').trim(),
            borderPrimary: rootStyles.getPropertyValue('--color-borderPrimary').trim(),
            borderSecondary: rootStyles.getPropertyValue('--color-borderSecondary').trim(),
            gradientPrimary: rootStyles.getPropertyValue('--color-gradientPrimary').trim(),
            gradientHover: rootStyles.getPropertyValue('--color-gradientHover').trim(),
          },
          typography: {
            fontFamily: rootStyles.getPropertyValue('--font-family').trim(),
            fontFamilyMono: rootStyles.getPropertyValue('--font-family-mono').trim(),
            fontSizeBase: rootStyles.getPropertyValue('--font-size-base').trim(),
            fontSizeScale: parseFloat(rootStyles.getPropertyValue('--font-size-scale').trim()),
            lineHeight: parseFloat(rootStyles.getPropertyValue('--line-height').trim()),
            letterSpacing: rootStyles.getPropertyValue('--letter-spacing').trim(),
          },
          spacing: {
            baseUnit: rootStyles.getPropertyValue('--spacing-base').trim(),
            scale: parseFloat(rootStyles.getPropertyValue('--spacing-scale').trim()),
            borderRadiusSmall: rootStyles.getPropertyValue('--radius-sm').trim(),
            borderRadiusMedium: rootStyles.getPropertyValue('--radius-md').trim(),
            borderRadiusLarge: rootStyles.getPropertyValue('--radius-lg').trim(),
          },
          brand: {
            siteName: rootStyles.getPropertyValue('--brand-site-name')?.trim() || '溫柔小姊姊｜選物 x 微批發',
            logoUrl: rootStyles.getPropertyValue('--brand-logo-url')?.trim() || '',
            faviconUrl: rootStyles.getPropertyValue('--brand-favicon-url')?.trim() || '',
            ogImageUrl: rootStyles.getPropertyValue('--brand-og-image-url')?.trim() || '',
          },
          shadows: {
            primary: rootStyles.getPropertyValue('--shadow-primary').trim(),
            primaryHover: rootStyles.getPropertyValue('--shadow-primaryHover').trim(),
            card: rootStyles.getPropertyValue('--shadow-card').trim(),
            cardHover: rootStyles.getPropertyValue('--shadow-cardHover').trim(),
            header: rootStyles.getPropertyValue('--shadow-header').trim(),
          },
          layout: {
            headerHeight: rootStyles.getPropertyValue('--header-height').trim(),
            maxContentWidth: rootStyles.getPropertyValue('--max-content-width').trim(),
            sidebarWidth: rootStyles.getPropertyValue('--sidebar-width')?.trim() || undefined,
          },
          metadata: {
            version: rootStyles.getPropertyValue('--theme-version')?.trim() || 'v1',
            deviceType,
            updatedAt: new Date().toISOString(),
          },
        };

        setTheme(themeConfig);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load theme';
        setError(errorMessage);
        console.error('Error loading theme:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, [deviceType]);

  const refreshTheme = async () => {
    const css = await getActiveThemeCSS(deviceType);
    injectThemeCSS(css);
    // Force re-parse by triggering a state update
    setError(null);
    const rootStyles = getComputedStyle(document.documentElement);
    const themeConfig: ThemeConfig = {
      colors: {
        primary: rootStyles.getPropertyValue('--color-primary').trim(),
        primaryHover: rootStyles.getPropertyValue('--color-primaryHover').trim(),
        primaryDark: rootStyles.getPropertyValue('--color-primaryDark').trim(),
        bgPrimary: rootStyles.getPropertyValue('--color-bgPrimary').trim(),
        bgSecondary: rootStyles.getPropertyValue('--color-bgSecondary').trim(),
        bgTertiary: rootStyles.getPropertyValue('--color-bgTertiary').trim(),
        bgLight: rootStyles.getPropertyValue('--color-bgLight').trim(),
        textPrimary: rootStyles.getPropertyValue('--color-textPrimary').trim(),
        textSecondary: rootStyles.getPropertyValue('--color-textSecondary').trim(),
        textTertiary: rootStyles.getPropertyValue('--color-textTertiary').trim(),
        textOnDark: rootStyles.getPropertyValue('--color-textOnDark').trim(),
        textOnDarkSecondary: rootStyles.getPropertyValue('--color-textOnDarkSecondary').trim(),
        borderPrimary: rootStyles.getPropertyValue('--color-borderPrimary').trim(),
        borderSecondary: rootStyles.getPropertyValue('--color-borderSecondary').trim(),
        gradientPrimary: rootStyles.getPropertyValue('--color-gradientPrimary').trim(),
        gradientHover: rootStyles.getPropertyValue('--color-gradientHover').trim(),
      },
      typography: {
        fontFamily: rootStyles.getPropertyValue('--font-family').trim(),
        fontFamilyMono: rootStyles.getPropertyValue('--font-family-mono').trim(),
        fontSizeBase: rootStyles.getPropertyValue('--font-size-base').trim(),
        fontSizeScale: parseFloat(rootStyles.getPropertyValue('--font-size-scale').trim() || '1.0'),
        lineHeight: parseFloat(rootStyles.getPropertyValue('--line-height').trim() || '1.5'),
        letterSpacing: rootStyles.getPropertyValue('--letter-spacing').trim(),
      },
      spacing: {
        baseUnit: rootStyles.getPropertyValue('--spacing-base').trim(),
        scale: parseFloat(rootStyles.getPropertyValue('--spacing-scale').trim() || '1.0'),
        borderRadiusSmall: rootStyles.getPropertyValue('--radius-sm').trim(),
        borderRadiusMedium: rootStyles.getPropertyValue('--radius-md').trim(),
        borderRadiusLarge: rootStyles.getPropertyValue('--radius-lg').trim(),
      },
      brand: {
        siteName: rootStyles.getPropertyValue('--site-name').trim(),
        logoUrl: rootStyles.getPropertyValue('--logo-url').trim(),
        faviconUrl: rootStyles.getPropertyValue('--favicon-url').trim(),
        ogImageUrl: rootStyles.getPropertyValue('--og-image-url').trim(),
      },
      shadows: {
        primary: rootStyles.getPropertyValue('--shadow-primary').trim(),
        primaryHover: rootStyles.getPropertyValue('--shadow-primaryHover').trim(),
        card: rootStyles.getPropertyValue('--shadow-card').trim(),
        cardHover: rootStyles.getPropertyValue('--shadow-cardHover').trim(),
        header: rootStyles.getPropertyValue('--shadow-header').trim(),
      },
      layout: {
        headerHeight: rootStyles.getPropertyValue('--header-height').trim(),
        maxContentWidth: rootStyles.getPropertyValue('--max-content-width').trim(),
      },
      metadata: {
        version: rootStyles.getPropertyValue('--theme-version').trim(),
        deviceType: rootStyles.getPropertyValue('--device-type').trim() as DeviceType,
        updatedAt: rootStyles.getPropertyValue('--theme-updated').trim(),
      },
    };
    setTheme(themeConfig);
  };

  const value: ThemeContextType = {
    theme,
    deviceType,
    isLoading,
    error,
    refreshTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
